import {
  ABN_FORMAT_OPTIONS,
  DATE_COMPARE_WITH_OPTIONS,
  DATE_COMPARISON_OPTIONS,
  DATE_FORMAT_OPTIONS,
  DATETIME_FORMAT_OPTIONS,
  DECIMAL_PRECISION_OPTIONS,
  DOMAIN_MODE_OPTIONS,
  EXTERNAL_SERVICE_OPTIONS,
  FORMAT_CHECK_OPTIONS,
  PHONE_COUNTRY_MODE_OPTIONS,
  PHONE_INPUT_OPTIONS,
  TIME_FORMAT_OPTIONS,
  VAL_BASE_CHECK_OPTIONS,
  VAL_CONDITION_TYPE_OPTIONS,
  VERIFICATION_CODE_FORMAT_OPTIONS,
  WEB_PROTOCOL_OPTIONS,
  YES_NO_OPTIONS,
  findDuplicateValidationRule,
  formatBaseCheckSettings,
  formatConditionSettings,
  getBaseCheckLabel,
  getBaseChecksForField,
  getConditionTypeLabel,
  getConditionTypesForField,
  getEntryPointsForModule,
  getFieldDefinition,
  getInputFieldsForEntry,
  getTriggerLabel,
  getTriggersForEntry,
  getValidationModuleLabel,
  namesMatch,
  nextValidationRuleId,
  normalizeValidationForm,
} from './validationConfig';
import { emitRuleSaveHistory } from './changeHistoryEmitters';

const ACTOR = {
  name: 'Olivia Brown',
  role: 'Configuration Admin',
  accountId: 'ADM-0002',
};

export function validationActor() {
  return { ...ACTOR };
}

function requiredMessage(field) {
  return `${field} is required.`;
}

function addError(result, key, message) {
  if (!result.summary.includes(message)) result.summary.push(message);
  if (key && !result.errors[key]) result.errors[key] = message;
}

function conditionFingerprint(condition) {
  return [
    condition.type || '',
    JSON.stringify(condition.settings || {}),
  ].join('|');
}

function hasBound(value) {
  return value !== '' && value != null;
}

function toNumber(value) {
  if (!hasBound(value)) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : NaN;
}

function baseCheckNeedsSettings(baseCheck) {
  return [
    'email_address',
    'web_address',
    'phone_number',
    'abn',
    'decimal_number',
    'date',
    'time',
    'date_and_time',
    'verification_code',
  ].includes(baseCheck);
}

function validateBaseCheckSettings(form, result) {
  const settings = form.baseCheckSettings || {};
  const check = form.baseCheck;
  if (check === 'email_address' || check === 'web_address') {
    if (!DOMAIN_MODE_OPTIONS.some((option) => option.value === settings.domainMode)) {
      addError(result, 'baseCheckSettings', 'Complete the Base Check settings.');
    }
    if (settings.domainMode === 'selected_domains') {
      const domains = (settings.domains || []).map((value) => String(value || '').trim()).filter(Boolean);
      if (!domains.length) addError(result, 'baseCheckSettings', 'Complete the Base Check settings.');
      if (!YES_NO_OPTIONS.some((option) => option.value === settings.allowSubdomains)) {
        addError(result, 'baseCheckSettings', 'Complete the Base Check settings.');
      }
    }
    if (check === 'web_address' && !WEB_PROTOCOL_OPTIONS.some((option) => option.value === settings.webProtocol)) {
      addError(result, 'baseCheckSettings', 'Complete the Base Check settings.');
    }
  }
  if (check === 'phone_number') {
    if (!PHONE_INPUT_OPTIONS.some((option) => option.value === settings.phoneInput)) {
      addError(result, 'baseCheckSettings', 'Complete the Base Check settings.');
    }
    if (!PHONE_COUNTRY_MODE_OPTIONS.some((option) => option.value === settings.phoneCountryMode)) {
      addError(result, 'baseCheckSettings', 'Complete the Base Check settings.');
    }
    if (settings.phoneCountryMode === 'selected_countries' && !(settings.countries || []).length) {
      addError(result, 'baseCheckSettings', 'Complete the Base Check settings.');
    }
    if (settings.phoneInput === 'national' && !settings.countryField && !(settings.countries || []).length) {
      addError(result, 'baseCheckSettings', 'Complete the Base Check settings.');
    }
  }
  if (check === 'abn' && !ABN_FORMAT_OPTIONS.some((option) => option.value === settings.abnFormat)) {
    addError(result, 'baseCheckSettings', 'Complete the Base Check settings.');
  }
  if (check === 'decimal_number') {
    if (!DECIMAL_PRECISION_OPTIONS.some((option) => option.value === settings.decimalPrecision)) {
      addError(result, 'baseCheckSettings', 'Complete the Base Check settings.');
    }
    if (settings.decimalPrecision === 'maximum') {
      const places = toNumber(settings.maxDecimalPlaces);
      if (!Number.isInteger(places) || places < 0) addError(result, 'baseCheckSettings', 'Enter a value in the required format.');
    }
  }
  if (check === 'date' && !DATE_FORMAT_OPTIONS.some((option) => option.value === settings.dateFormat)) {
    addError(result, 'baseCheckSettings', 'Complete the Base Check settings.');
  }
  if (check === 'time' && !TIME_FORMAT_OPTIONS.some((option) => option.value === settings.timeFormat)) {
    addError(result, 'baseCheckSettings', 'Complete the Base Check settings.');
  }
  if (check === 'date_and_time' && !DATETIME_FORMAT_OPTIONS.some((option) => option.value === settings.dateTimeFormat)) {
    addError(result, 'baseCheckSettings', 'Complete the Base Check settings.');
  }
  if (check === 'verification_code' && !VERIFICATION_CODE_FORMAT_OPTIONS.some((option) => option.value === settings.verificationCodeFormat)) {
    addError(result, 'baseCheckSettings', 'Complete the Base Check settings.');
  }
}

function validateConditionSettings(condition, form, result, index) {
  const key = `condition_${index}`;
  const settings = condition.settings || {};
  const type = condition.type;
  const fieldDef = getFieldDefinition(form.entryPoint, form.inputField);

  if (type === 'format_check' && !FORMAT_CHECK_OPTIONS.some((option) => option.value === settings.formatOption)) {
    addError(result, key, 'Complete or remove this Condition.');
  }
  if (type === 'character_limit_check') {
    const min = toNumber(settings.minChars);
    const max = toNumber(settings.maxChars);
    if (!hasBound(settings.minChars) && !hasBound(settings.maxChars)) addError(result, key, 'Complete or remove this Condition.');
    if (hasBound(settings.minChars) && (!Number.isInteger(min) || min < 0)) addError(result, key, 'Enter a value in the required format.');
    if (hasBound(settings.maxChars) && (!Number.isInteger(max) || max < 1)) addError(result, key, 'Enter a value in the required format.');
    if (hasBound(settings.minChars) && hasBound(settings.maxChars) && min > max) addError(result, key, 'Minimum cannot be higher than Maximum.');
  }
  if (type === 'number_range_check') {
    if (!hasBound(settings.minNum) && !hasBound(settings.maxNum)) addError(result, key, 'Complete or remove this Condition.');
    const min = toNumber(settings.minNum);
    const max = toNumber(settings.maxNum);
    if (hasBound(settings.minNum) && Number.isNaN(min)) addError(result, key, 'Enter a value in the required format.');
    if (hasBound(settings.maxNum) && Number.isNaN(max)) addError(result, key, 'Enter a value in the required format.');
    if (hasBound(settings.minNum) && hasBound(settings.maxNum) && min > max) addError(result, key, 'Minimum cannot be higher than Maximum.');
    if (hasBound(settings.minNum) && hasBound(settings.maxNum) && min === max && (settings.minInclude !== 'include' || settings.maxInclude !== 'include')) {
      addError(result, key, 'These checks cannot be true together.');
    }
  }
  if (type === 'file_type_check') {
    if (!(settings.fileTypes || []).length) addError(result, key, 'Complete or remove this Condition.');
    const allowed = fieldDef.fileTypes || [];
    if ((settings.fileTypes || []).some((value) => allowed.length && !allowed.includes(value))) {
      addError(result, key, 'This selection is unavailable or incompatible.');
    }
  }
  if (type === 'file_count_check') {
    if (!hasBound(settings.minFiles) && !hasBound(settings.maxFiles)) addError(result, key, 'Complete or remove this Condition.');
    const min = toNumber(settings.minFiles);
    const max = toNumber(settings.maxFiles);
    if (hasBound(settings.minFiles) && (!Number.isInteger(min) || min < 0)) addError(result, key, 'Enter a value in the required format.');
    if (hasBound(settings.maxFiles) && (!Number.isInteger(max) || max < 1)) addError(result, key, 'Enter a value in the required format.');
    if (hasBound(settings.minFiles) && hasBound(settings.maxFiles) && min > max) addError(result, key, 'Minimum cannot be higher than Maximum.');
  }
  if (type === 'file_size_check') {
    if (!hasBound(settings.minSize) && !hasBound(settings.maxSize)) addError(result, key, 'Complete or remove this Condition.');
    const min = toNumber(settings.minSize);
    const max = toNumber(settings.maxSize);
    if (hasBound(settings.maxSize) && (!(max > 0))) addError(result, key, 'Enter a value in the required format.');
    if (hasBound(settings.minSize) && hasBound(settings.maxSize) && min > max) addError(result, key, 'Minimum cannot be higher than Maximum.');
  }
  if (type === 'image_dimensions_check') {
    const hasDim = ['minWidth', 'maxWidth', 'minHeight', 'maxHeight', 'aspectRatios'].some((field) => hasBound(settings[field]));
    if (!hasDim) addError(result, key, 'Complete or remove this Condition.');
    const minW = toNumber(settings.minWidth);
    const maxW = toNumber(settings.maxWidth);
    if (hasBound(settings.minWidth) && hasBound(settings.maxWidth) && minW > maxW) addError(result, key, 'Minimum cannot be higher than Maximum.');
  }
  if (type === 'allowed_value_check' && !(settings.allowedValues || []).length && !settings.allowedSource) {
    addError(result, key, 'Complete or remove this Condition.');
  }
  if (type === 'date_check') {
    if (!DATE_COMPARISON_OPTIONS.some((option) => option.value === settings.comparison) || !DATE_COMPARE_WITH_OPTIONS.some((option) => option.value === settings.compareWith)) {
      addError(result, key, 'Complete or remove this Condition.');
    }
    if (settings.compareWith === 'fixed_date_time' && !String(settings.fixedDateTime || '').trim()) {
      addError(result, key, 'Complete or remove this Condition.');
    }
    if (settings.compareWith === 'another_compatible_field') {
      if (!settings.compareField) addError(result, key, 'Complete or remove this Condition.');
      if (settings.compareField === form.inputField) addError(result, key, 'This selection is unavailable or incompatible.');
    }
    if ((settings.compareWith === 'current_date_time' || settings.compareWith === 'another_compatible_field') && !settings.timeZone) {
      addError(result, key, 'Complete or remove this Condition.');
    }
    if (settings.adjustment && settings.adjustment !== 'no_adjustment') {
      const amount = toNumber(settings.adjustAmount);
      if (!Number.isInteger(amount) || amount < 1 || !settings.adjustUnit) addError(result, key, 'Complete or remove this Condition.');
    }
  }
  if (type === 'duplicate_check' && (!settings.duplicateSource || !settings.duplicateScope || !settings.duplicateField)) {
    addError(result, key, 'Complete or remove this Condition.');
  }
  if (type === 'relationship_check') {
    if (!settings.relationship) addError(result, key, 'Complete or remove this Condition.');
    if (['matches_another_field', 'differs_from_another_field'].includes(settings.relationship)) {
      if (!settings.relatedField) addError(result, key, 'Complete or remove this Condition.');
      if (settings.relatedField === form.inputField) addError(result, key, 'This selection is unavailable or incompatible.');
    }
  }
  if (type === 'verification_code_check' && !settings.verificationSource) {
    addError(result, key, 'Complete or remove this Condition.');
  }
  if (type === 'external_check') {
    const service = EXTERNAL_SERVICE_OPTIONS.find((option) => option.value === settings.externalService);
    if (!service || !service.enabled) addError(result, key, 'This selection is unavailable or incompatible.');
    if (!settings.externalCheck || !(settings.acceptedResults || []).length) addError(result, key, 'Complete or remove this Condition.');
  }
}

export function collectValidationSaveErrors(formData, allRules, excludeId, { originalLastUpdated } = {}) {
  const form = normalizeValidationForm(formData);
  const result = { errors: {}, summary: [] };
  const others = (allRules || []).filter((rule) => rule.id !== excludeId).map(normalizeValidationForm);

  if (!String(form.ruleName || '').trim()) addError(result, 'ruleName', requiredMessage('Rule Name'));
  else if (others.some((rule) => namesMatch(rule.ruleName, form.ruleName))) {
    addError(result, 'ruleName', 'Enter a unique Rule Name.');
  }

  if (!form.module || !form.entryPoint || !form.trigger || !form.inputField) {
    addError(result, 'module', 'Select a Module, Entry Point, Trigger and Input Field.');
    if (!form.module) addError(result, 'module', requiredMessage('Module'));
    if (!form.entryPoint) addError(result, 'entryPoint', requiredMessage('Entry Point'));
    if (!form.trigger) addError(result, 'trigger', requiredMessage('Trigger'));
    if (!form.inputField) addError(result, 'inputField', requiredMessage('Input Field'));
  }

  const entryOptions = getEntryPointsForModule(form.module);
  if (form.module && form.entryPoint && !entryOptions.some((option) => option.value === form.entryPoint)) {
    addError(result, 'entryPoint', 'This selection is unavailable or incompatible.');
  }
  const triggerOptions = getTriggersForEntry(form.entryPoint);
  if (form.trigger && !triggerOptions.some((option) => option.value === form.trigger)) {
    addError(result, 'trigger', 'This selection is unavailable or incompatible.');
  }
  const fieldOptions = getInputFieldsForEntry(form.entryPoint);
  if (form.inputField && !fieldOptions.some((option) => option.value === form.inputField)) {
    addError(result, 'inputField', 'This selection is unavailable or incompatible.');
  }

  const baseOptions = getBaseChecksForField(form.inputField);
  if (!form.baseCheck) addError(result, 'baseCheck', 'Select a compatible Base Check.');
  else if (!baseOptions.some((option) => option.value === form.baseCheck)) {
    addError(result, 'baseCheck', 'Select a compatible Base Check.');
  }
  if (form.baseCheck && baseCheckNeedsSettings(form.baseCheck)) validateBaseCheckSettings(form, result);
  if (!String(form.baseCheckMessage || '').trim()) addError(result, 'baseCheckMessage', 'Enter a Base Check Message.');
  if (!String(form.changeReason || '').trim()) addError(result, 'changeReason', 'Enter a Change Reason.');

  const dup = findDuplicateValidationRule(others, form);
  if (dup) addError(result, 'duplicate', `A Validation Rule already exists for this scope: ${dup.id}.`);

  const seenNames = [];
  const seenFingerprints = [];
  (form.conditions || []).forEach((condition, index) => {
    const key = `condition_${index}`;
    if (!condition.type || !String(condition.name || '').trim() || !String(condition.message || '').trim()) {
      addError(result, key, 'Complete or remove this Condition.');
    }
    if (String(condition.name || '').trim()) {
      if (seenNames.some((name) => namesMatch(name, condition.name))) addError(result, key, 'Enter a unique Condition Name.');
      seenNames.push(condition.name);
    }
    if (!String(condition.message || '').trim() && condition.type) {
      addError(result, `${key}_message`, 'Enter a Validation Message for this Condition.');
    }
    const typeOptions = getConditionTypesForField(form.inputField, form.baseCheck);
    if (condition.type && !typeOptions.some((option) => option.value === condition.type)) {
      addError(result, key, 'This selection is unavailable or incompatible.');
    }
    if (condition.type) validateConditionSettings(condition, form, result, index);
    const fingerprint = conditionFingerprint(condition);
    if (condition.type && seenFingerprints.includes(fingerprint)) addError(result, key, 'This Condition is already included.');
    if (condition.type) seenFingerprints.push(fingerprint);
  });

  const formatOptions = (form.conditions || []).filter((condition) => condition.type === 'format_check').map((condition) => condition.settings?.formatOption);
  if (formatOptions.includes('letters_only') && formatOptions.includes('digits_only')) {
    addError(result, 'conditions', 'These checks cannot be true together.');
  }
  if (formatOptions.includes('no_whitespace') && formatOptions.includes('letters_and_spaces')) {
    addError(result, 'conditions', 'These checks cannot be true together.');
  }

  if (excludeId && originalLastUpdated) {
    const current = (allRules || []).find((rule) => rule.id === excludeId);
    if (current && (current.lastUpdated || current.modifiedOn) && (current.lastUpdated || current.modifiedOn) !== originalLastUpdated) {
      addError(result, 'concurrent', 'This Rule was updated by another Team Member. Review the current Rule before saving.');
    }
  }

  return result;
}

export function ruleSnapshotFromForm(formData) {
  const form = normalizeValidationForm(formData);
  return {
    ruleName: String(form.ruleName || '').trim(),
    module: form.module,
    entryPoint: form.entryPoint,
    trigger: form.trigger,
    inputField: form.inputField,
    baseCheck: form.baseCheck,
    baseCheckSettings: { ...(form.baseCheckSettings || {}) },
    baseCheckMessage: String(form.baseCheckMessage || '').trim(),
    conditions: (form.conditions || []).map((condition) => ({
      name: String(condition.name || '').trim(),
      type: condition.type,
      settings: { ...(condition.settings || {}) },
      message: String(condition.message || '').trim(),
    })),
  };
}

export function getPublishedRule(rule) {
  if (!rule) return null;
  const { pendingDraft, ...published } = rule;
  return normalizeValidationForm(published);
}

export function getEditRuleSource(rule) {
  if (!rule) return null;
  return normalizeValidationForm(rule);
}

export function appendTestHistory(rule, summary, passed) {
  const actor = validationActor();
  const base = Array.isArray(rule.testHistory) ? rule.testHistory : [];
  return [
    ...base,
    {
      actor: actor.name,
      role: actor.role,
      accountId: actor.accountId,
      timestamp: new Date().toISOString(),
      result: passed ? 'passed' : 'blocked',
      summary,
    },
  ];
}

export function mergeRulesAfterSave(allRules, payload, publishedBaseline) {
  const snapshot = ruleSnapshotFromForm(payload);
  const isNew = !publishedBaseline?.id;
  const nowIso = payload.lastUpdated || new Date().toISOString();
  const actor = payload.lastUpdatedBy || ACTOR.name;
  const id = payload.id || publishedBaseline?.id || nextValidationRuleId(allRules);
  const record = {
    ...snapshot,
    id,
    dateAdded: isNew ? nowIso : publishedBaseline.dateAdded || publishedBaseline.createdOn || nowIso,
    addedBy: isNew ? actor : publishedBaseline.addedBy || publishedBaseline.createdBy || actor,
    lastUpdated: nowIso,
    lastUpdatedBy: actor,
    changeReason: String(payload.changeReason || '').trim(),
    testHistory: payload.testHistory || publishedBaseline?.testHistory || [],
    configurationHistory: payload.configurationHistory || publishedBaseline?.configurationHistory || [],
  };
  try {
    emitRuleSaveHistory({
      isNew,
      previous: publishedBaseline,
      next: record,
      module: 'Configuration — Validation',
      sourceModuleRef: 'configuration_validation',
      changedItem: record.ruleName || record.name || id,
      itemId: id,
      changeReason: record.changeReason,
      actor,
      stamp: nowIso,
      compareKeys: ['ruleName', 'name', 'status', 'module', 'conditions', 'configurationStatus'],
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }
  if (isNew) return [...allRules.filter((rule) => rule.id !== record.id), record];
  return allRules.map((rule) => (rule.id === record.id ? { ...rule, ...record } : rule));
}

export function mergeRulesAfterActivate(allRules, payload, publishedBaseline) {
  return mergeRulesAfterSave(allRules, payload, publishedBaseline);
}

export function collectActivationErrors(formData, allRules, excludeId) {
  return collectValidationSaveErrors(formData, allRules, excludeId).summary;
}

export function runValidationRuleTest(formData, allRules) {
  const snapshot = ruleSnapshotFromForm(formData);
  const validation = collectValidationSaveErrors(formData, allRules, formData.id || undefined);
  if (validation.summary.length) {
    return {
      passed: false,
      summary: 'Rule test blocked. Configuration conflicts or missing requirements were found.',
      detail: validation.summary.map((message) => `• ${message}`).join('\n'),
    };
  }
  return {
    passed: true,
    summary: 'Rule test passed. Configuration is valid — no live input was checked.',
    detail: [
      `Module: ${getValidationModuleLabel(snapshot.module)}`,
      `Entry Point: ${snapshot.entryPoint}`,
      `Trigger: ${getTriggerLabel(snapshot.trigger)}`,
      `Input Field: ${snapshot.inputField}`,
      `Base Check: ${getBaseCheckLabel(snapshot.baseCheck)}`,
      '',
      'No duplicate-scope or impossible-check issues detected.',
    ].join('\n'),
  };
}

export function fieldLabel(key) {
  const labels = {
    ruleName: 'Rule Name',
    module: 'Module',
    entryPoint: 'Entry Point',
    trigger: 'Trigger',
    inputField: 'Input Field',
    baseCheck: 'Base Check',
    baseCheckMessage: 'Base Check Message',
  };
  return labels[key] || key;
}

export function displayFieldValue(key, value) {
  if (value == null || value === '') return '—';
  if (key === 'module') return getValidationModuleLabel(value);
  if (key === 'trigger') return getTriggerLabel(value);
  if (key === 'baseCheck') return getBaseCheckLabel(value);
  return String(value);
}

export { formatBaseCheckSettings, formatConditionSettings, getConditionTypeLabel, VAL_BASE_CHECK_OPTIONS, VAL_CONDITION_TYPE_OPTIONS };
